import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSibling } from '../../../actions/programModules';
import TreeRow from './TreeRow';
import TreeItemForm from './TreeItemForm';
import styles from './styles.module.scss';

// Иконки
const EmptyIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
    <line x1="9" y1="14" x2="15" y2="14" />
  </svg>
);

const ExpandAllIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="7 13 12 18 17 13" />
    <polyline points="7 6 12 11 17 6" />
  </svg>
);

const CollapseAllIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="17 11 12 6 7 11" />
    <polyline points="17 18 12 13 7 18" />
  </svg>
);

const Spinner = ({ size = 40 }) => (
  <svg
    className={styles.spinner}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
  >
    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
  </svg>
);

const INDENT_SIZE = 32;

// Функция для сбора всех OID узлов с детьми (для expandAll)
const collectExpandableIds = (nodes, result = new Set()) => {
  nodes.forEach(node => {
    if (node.children && node.children.length > 0) {
      result.add(node.oid);
      collectExpandableIds(node.children, result);
    }
  });
  return result;
};

// Функция для "плоского" представления дерева с учётом expanded состояния
const flattenTree = (nodes, expandedIds, level = 0, result = [], rowIndex = { current: 0 }, connectorLevels = [], parentOid = 0) => {
  const totalNodes = nodes.length;

  nodes.forEach((node, idx) => {
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expandedIds.has(node.oid);
    const isLastChild = idx === totalNodes - 1;
    const isFirst = idx === 0;
    const currentConnectors = [...connectorLevels];

    result.push({
      ...node,
      level,
      hasChildren,
      isExpanded,
      isLastChild,
      isFirst,
      connectorLevels: currentConnectors,
      rowIndex: rowIndex.current++,
      parentOid,
      siblings: nodes,
    });

    // Добавляем дочерние только если узел раскрыт
    if (hasChildren && isExpanded) {
      const nextConnectors = [...currentConnectors];
      if (!isLastChild) {
        nextConnectors.push(level);
      }
      flattenTree(node.children, expandedIds, level + 1, result, rowIndex, nextConnectors, node.oid);
    }
  });

  return result;
};

const SCROLL_ZONE = 60; // Зона у краёв для авто-скролла (px)
const SCROLL_SPEED = 8; // Скорость скролла (px за кадр)

const Tree = () => {
  const dispatch = useDispatch();
  const tree = useSelector((state) => state.programModules.tree);
  const cid = useSelector((state) => state.programModules.cid);
  const loading = useSelector((state) => state.programModules.loading);

  const [showAddRoot, setShowAddRoot] = useState(false);
  // По умолчанию всё свёрнуто (пустой Set)
  const [expandedIds, setExpandedIds] = useState(() => new Set());

  const gridBodyRef = useRef(null);
  const scrollAnimationRef = useRef(null);

  const flatNodes = useMemo(() => {
    return flattenTree(tree, expandedIds);
  }, [tree, expandedIds]);

  // Авто-скролл при драге у краёв
  useEffect(() => {
    const gridBody = gridBodyRef.current;
    if (!gridBody) return;

    const handleDragOver = (e) => {
      const rect = gridBody.getBoundingClientRect();
      const y = e.clientY;

      // Отменяем предыдущую анимацию
      if (scrollAnimationRef.current) {
        cancelAnimationFrame(scrollAnimationRef.current);
        scrollAnimationRef.current = null;
      }

      // Скролл вверх
      if (y < rect.top + SCROLL_ZONE && gridBody.scrollTop > 0) {
        const scrollUp = () => {
          gridBody.scrollTop -= SCROLL_SPEED;
          if (gridBody.scrollTop > 0) {
            scrollAnimationRef.current = requestAnimationFrame(scrollUp);
          }
        };
        scrollAnimationRef.current = requestAnimationFrame(scrollUp);
      }
      // Скролл вниз
      else if (y > rect.bottom - SCROLL_ZONE &&
               gridBody.scrollTop < gridBody.scrollHeight - gridBody.clientHeight) {
        const scrollDown = () => {
          gridBody.scrollTop += SCROLL_SPEED;
          if (gridBody.scrollTop < gridBody.scrollHeight - gridBody.clientHeight) {
            scrollAnimationRef.current = requestAnimationFrame(scrollDown);
          }
        };
        scrollAnimationRef.current = requestAnimationFrame(scrollDown);
      }
    };

    const handleDragLeave = () => {
      if (scrollAnimationRef.current) {
        cancelAnimationFrame(scrollAnimationRef.current);
        scrollAnimationRef.current = null;
      }
    };

    const handleDrop = () => {
      if (scrollAnimationRef.current) {
        cancelAnimationFrame(scrollAnimationRef.current);
        scrollAnimationRef.current = null;
      }
    };

    gridBody.addEventListener('dragover', handleDragOver);
    gridBody.addEventListener('dragleave', handleDragLeave);
    gridBody.addEventListener('drop', handleDrop);

    return () => {
      gridBody.removeEventListener('dragover', handleDragOver);
      gridBody.removeEventListener('dragleave', handleDragLeave);
      gridBody.removeEventListener('drop', handleDrop);
      if (scrollAnimationRef.current) {
        cancelAnimationFrame(scrollAnimationRef.current);
      }
    };
  }, []);

  const handleAddRoot = (formData) => {
    dispatch(createSibling(0, formData));
    setShowAddRoot(false);
  };

  const toggleExpand = useCallback((oid) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(oid)) {
        next.delete(oid);
      } else {
        next.add(oid);
      }
      return next;
    });
  }, []);

  const expandAll = useCallback(() => {
    setExpandedIds(collectExpandableIds(tree));
  }, [tree]);

  const collapseAll = useCallback(() => {
    setExpandedIds(new Set());
  }, []);

  if (!cid) {
    return null;
  }

  // Состояние загрузки
  if (loading && tree.length === 0) {
    return (
      <div className={styles.tree}>
        <div className={styles.loadingState}>
          <Spinner size={40} />
          <p>Загрузка данных...</p>
        </div>
      </div>
    );
  }

  // Пустое состояние
  if (tree.length === 0) {
    return (
      <div className={styles.tree}>
        <div className={styles.emptyState}>
          <EmptyIcon />
          <p>Нет разделов и тем</p>
          <button
            className={styles.addRootButton}
            onClick={() => setShowAddRoot(true)}
          >
            Добавить первый раздел
          </button>
        </div>

        {showAddRoot && (
          <TreeItemForm
            title="Добавить раздел"
            onSubmit={handleAddRoot}
            onClose={() => setShowAddRoot(false)}
          />
        )}
      </div>
    );
  }

  return (
    <div className={styles.tree}>
      <div className={styles.card}>
        {/* Toolbar */}
        <div className={styles.toolbar}>
          <button
            className={styles.toolbarButton}
            onClick={expandAll}
            disabled={loading}
            type="button"
          >
            <ExpandAllIcon />
            Раскрыть всё
          </button>
          <button
            className={styles.toolbarButton}
            onClick={collapseAll}
            disabled={loading}
            type="button"
          >
            <CollapseAllIcon />
            Свернуть всё
          </button>
        </div>

        {/* Шапка */}
        <div className={styles.gridHeader}>
          <div className={styles.gridHeaderCell}>Индекс</div>
          <div className={styles.gridHeaderCell}>Наименование</div>
          <div className={styles.gridHeaderCell}>Описание</div>
          <div className={styles.gridHeaderCell}>Действия</div>
        </div>

        {/* Тело */}
        <div className={styles.gridBody} ref={gridBodyRef}>
          {flatNodes.map((node, index) => (
            <TreeRow
              key={node.oid}
              node={node}
              index={index}
              indentSize={INDENT_SIZE}
              onToggleExpand={toggleExpand}
            />
          ))}
        </div>
      </div>

      {showAddRoot && (
        <TreeItemForm
          title="Добавить раздел"
          onSubmit={handleAddRoot}
          onClose={() => setShowAddRoot(false)}
        />
      )}
    </div>
  );
};

export default Tree;
